import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { forkJoin, takeUntil, tap } from 'rxjs';
import { tryParseToNumber } from '../../../../../../core/functions/string-helpers';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { CoordinatesInCanadianBorderRegionRequest } from '../../../../../../core/services/tools/models/coordinates-in-canadian-border-region-request';
import { ToolsService } from '../../../../../../core/services/tools/tools.service';
import { ApplicationFormFieldComponent } from '../../../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { AttachmentRequiredButtonComponent } from '../../../shared/components/attachment/attachment-required-button/attachment-required-button.component';
import { ScheduleD } from '../../models/schedule-d';
import { ScheduleDStepBaseComponent } from '../schedule-d-step-base/schedule-d-step-base.component';
import { ScheduleDStepThreeHelperMessages } from './constants/schedule-d-step-three-helper-messages';
import { ScheduleDStepThreeService } from './services/schedule-d-step-three/schedule-d-step-three.service';

@Component({
    selector: 'app-schedule-d-step-three',
    imports: [
        MatSelectModule,
        ApplicationFormFieldComponent,
        MatFormFieldModule,
        FormsModule,
        MatInputModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatIconModule,
        MatDatepickerModule,
        AttachmentRequiredButtonComponent,
    ],
    providers: [provideNativeDateAdapter()],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './schedule-d-step-three.component.html',
})
export class ScheduleDStepThreeComponent
    extends ScheduleDStepBaseComponent
    implements OnInit, AfterViewInit
{
    private readonly dropdownService = inject(DropdownService);
    private readonly stepService = inject(ScheduleDStepThreeService);
    private readonly toolsService = inject(ToolsService);

    // Static is false because the inputElement is conditionally rendered
    // First control on this step is siteStatus, so we focus it by default
    @ViewChild('siteStatusField', { static: false })
    siteStatusField: MatSelect | undefined;

    latitudeLongitudeInDecimalFormat: boolean = false;

    messages = ScheduleDStepThreeHelperMessages;

    siteStatusOptions: DropdownOption[] = [];
    clearanceIndicatorOptions: DropdownOption[] = [];
    quietZoneConsentOptions: DropdownOption[] = [];
    nepaOptions: DropdownOption[] = [];
    mexicoClearanceIndicatorOptions: DropdownOption[] = [];

    regExPattern = RegExPattern;

    ngOnInit(): void {
        this.loadDropdownData();
        this.setInitialClearanceIndicatorValueIfApplicable();
    }

    ngAfterViewInit(): void {
        if (this.canHaveField('siteStatus')) {
            setTimeout(() => {
                this.siteStatusField?.focus();
            });
        }
    }

    /**
     * Returns whether the form should show a field based on the type of schedule d.
     * @param field the field to check.
     * @returns true if the form should show the field, otherwise false.
     */
    canHaveField(field: keyof ScheduleD): boolean {
        return this.stepService.canHaveField(this.form, field);
    }

    /**
     * If this is a new location and the clearance indicator field is applicable, sets the initial value of
     * the clearance indicator field based on whether the coordinates are in the Canadian border region.
     */
    private setInitialClearanceIndicatorValueIfApplicable(): void {
        if (
            this.form.controls.actionCode.value !== ActionCode.Add ||
            !this.canHaveField('clearanceIndicator')
        ) {
            return;
        }

        const clearanceIndicatorControl = this.form.controls.clearanceIndicator;

        const latitude = tryParseToNumber(this.form.controls.latitude.value);
        const longitude = tryParseToNumber(this.form.controls.longitude.value);

        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            const request: CoordinatesInCanadianBorderRegionRequest = {
                latitude: latitude!,
                longitude: longitude!,
            };

            this.toolsService
                .coordinatesInCanadianBorderRegion(request)
                .pipe(takeUntil(this.destroy$))
                .subscribe((response) => {
                    if (!response.isSuccess) {
                        if (response.errorMessage) {
                            console.error(response.errorMessage);
                        }

                        return;
                    }

                    clearanceIndicatorControl.setValue(
                        response.coordinatesAreInCanadianBorderRegion,
                    );
                });
        } else {
            clearanceIndicatorControl.setValue(false);
        }
    }

    private loadDropdownData() {
        forkJoin({
            siteStatusOptions: this.dropdownService.getSiteStatusCodes(),
            yesNoOptions: this.dropdownService.getYesNoOptions(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    ({
                        siteStatusOptions: siteStatusOptions,
                        yesNoOptions: yesNoOptions,
                    }) => {
                        this.siteStatusOptions = siteStatusOptions;
                        this.clearanceIndicatorOptions = yesNoOptions;
                        this.quietZoneConsentOptions = yesNoOptions;
                        this.nepaOptions = yesNoOptions;
                        this.mexicoClearanceIndicatorOptions = yesNoOptions;
                    },
                ),
            )
            .subscribe();
    }
}
